import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './api-base-url';
import {
  isRenewableTokenRejection,
  refreshSession,
  resolveRequestAuth,
  type SessionTokenStore,
} from './token-refresh';

// API Constants
const API_TIMEOUT = 30000; // 30 seconds

// Create axios instance
// NOTE: Do NOT set a default Content-Type header. Axios auto-detects:
//   - plain object -> 'application/json'
//   - FormData     -> 'multipart/form-data; boundary=<unique>'
// An instance-level 'application/json' default clobbers FormData uploads
// because axios will not generate the multipart boundary when Content-Type
// is already set, causing multer to reject the upload as "file is required".
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Respect an Authorization header the caller set explicitly. The
    // customer portal passes its OWN session token per request
    // (`customerApi(session.accessToken)` / direct `fetch`s with
    // `Bearer ${session.accessToken}`). During Switch-User impersonation
    // the operator's admin token stays in localStorage so Exit can
    // restore it — so blindly overwriting here would send the admin
    // token on portal requests, resolving to no customer context and an
    // empty portal. Only fall back to the stored token when the caller
    // hasn't already provided one.
    //
    // ATL-001144: the session the token belongs to is recorded on the config
    // so a 401 that rejected the token (expired / revoked) can be renewed and
    // retried with that session's refresh token. A portal
    // caller may still hold its login-time token after a renewal; it is
    // swapped for the session's current one here.
    const explicit = config.headers.Authorization;
    const explicitToken =
      typeof explicit === 'string' && explicit.startsWith('Bearer ') ? explicit.slice(7) : null;
    if (!explicit || explicitToken) {
      const auth = resolveRequestAuth(explicitToken);
      if (auth) {
        config.headers.Authorization = `Bearer ${auth.token}`;
        (config as AuthAwareRequestConfig).authStore = auth.store;
      }
    }
    // i18n: advertise the user's active UI language so the server can render
    // recipient-facing artifacts (emails, PDFs, SMS) and localized responses
    // in the same language. Sourced from the same localStorage key i18n.ts
    // writes; falls back to the request default when unset.
    if (!config.headers['Accept-Language']) {
      try {
        const lang = localStorage.getItem('waypoint_language');
        if (lang) config.headers['Accept-Language'] = lang;
      } catch {
        /* localStorage unavailable — omit the header */
      }
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/** Request config annotated with the session its bearer token belongs to (ATL-001144). */
interface AuthAwareRequestConfig extends InternalAxiosRequestConfig {
  authStore?: SessionTokenStore;
  authRetried?: boolean;
}

function blobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  // Older engines without Blob.text().
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * A JSON error body, parsed when the request asked for a Blob or text
 * (file downloads, CSV exports) and axios therefore did not parse it.
 */
async function readErrorBody(data: unknown): Promise<unknown> {
  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) return JSON.parse(await blobText(data));
    if (typeof data === 'string') return JSON.parse(data);
  } catch {
    /* not JSON */
  }
  return data;
}

function bearerOf(config: InternalAxiosRequestConfig): string | null {
  const header = config.headers?.Authorization;
  return typeof header === 'string' && header.startsWith('Bearer ') ? header.slice(7) : null;
}

// Flag to suppress 401 logout during demo reset
let suppressAuthRedirect = false;
export function setSuppressAuthRedirect(val: boolean) { suppressAuthRedirect = val; }
export function isAuthRedirectSuppressed(): boolean { return suppressAuthRedirect; }

/**
 * Event fired when the session is no longer valid (401 from API or proactive
 * TTL expiry). A listener mounted under the Router handles store cleanup +
 * navigation, so we stay in SPA-land instead of forcing a full reload.
 */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

// 'session-invalid' — the token authenticated but references a user that no
// longer exists in the DB (bootstrap 404 NOT_FOUND); treated like 'unauthorized'
// (clear session + redirect to login) so a stale token self-heals.
export type AuthExpiredReason = 'unauthorized' | 'token-ttl' | 'session-invalid';

export function dispatchAuthExpired(reason: AuthExpiredReason) {
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { reason } }));
}

// Response interceptor - Handle auth errors
//
// ATL-001144 (SEC-035): access tokens live 15 minutes. A 401 that rejected
// the access token itself (expired, or revoked by the user's logout on another
// device) is renewed once through /auth/refresh — shared by every request that
// fails at the same moment, see token-refresh.ts — and the request retried.
// Any other 401, a failed renewal, or a retry that 401s again ends the
// session exactly as before.
// DAT - ATL-001438 (C5): a load action (save, status change, bid accept) that
// covered or closed a load with a DAT post returns `datCover` in its body.
// Re-broadcast it so <DatCoverListener /> refreshes the badge and, in Prompt
// mode, offers "Remove from DAT" — whichever screen made the call.
export const DAT_COVER_EVENT = 'dat:cover';

api.interceptors.response.use(
  (response) => {
    const datCover = (response.data as { datCover?: unknown } | undefined)?.datCover;
    if (datCover && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(DAT_COVER_EVENT, { detail: datCover }));
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as AuthAwareRequestConfig | undefined;
    if (error.response?.status === 401 && !suppressAuthRedirect) {
      const sentToken = config ? bearerOf(config) : null;
      if (
        config?.authStore &&
        !config.authRetried &&
        isRenewableTokenRejection(await readErrorBody(error.response.data))
      ) {
        config.authRetried = true;
        const outcome = await refreshSession(config.authStore, sentToken);
        if (outcome.status === 'renewed') {
          config.headers.Authorization = `Bearer ${outcome.token}`;
          return api(config);
        }
        // Renewal could not reach the server (offline, 5xx, rate limit):
        // fail this request but keep the session — nothing was spent.
        if (outcome.status === 'transient') return Promise.reject(error);
      }
      dispatchAuthExpired('unauthorized');
    }
    return Promise.reject(error);
  }
);

export default api;
